/**
 * Provides an administrative interface for managing Task Priorities widget
 * configuration settings and Fluxx integration behavior. Allows administrators
 * to configure user-based task filtering, maintain Fluxx navigation URLs for
 * individual task records and "View in Fluxx" actions, and control how task
 * priority information is displayed to end users.
 *
 * The component retrieves existing widget configuration values, supports
 * updating task-related integration settings, tracks unsaved changes, and
 * enables administrators to save or restore Task Priorities configuration
 * settings used throughout the dashboard experience.
 */
import React, { useEffect, useState } from "react";
import {
  getWidgetConfigurations,
  updateWidgetConfigurations,
} from "@/services/widgetConfig";

const Toggle = ({
  enabled,
  onToggle,
}: {
  enabled: boolean;
  onToggle: (value: boolean) => void;
}) => (
  <div className="flex border border-gray-300 rounded overflow-hidden">
    <button
      type="button"
      className={`px-3 py-1 text-xs ${
        enabled
          ? "bg-[var(--color-brand)] text-white"
          : "bg-gray-100 text-gray-600"
      }`}
      onClick={() => onToggle(true)}
    >
      Enable
    </button>

    <button
      type="button"
      className={`px-3 py-1 text-xs ${
        !enabled
          ? "bg-[var(--color-brand)] text-white"
          : "bg-gray-100 text-gray-600"
      }`}
      onClick={() => onToggle(false)}
    >
      Disable
    </button>
  </div>
);

const FEATURE_CODE = "dashboard.taskpriorities";

const TaskPrioritiesConfig: React.FC = () => {
  const [savedConfig, setSavedConfig] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const [taskPrioritiesFluxxUrl1, setTaskPrioritiesFluxxUrl1] =
    useState("");
  const [taskPrioritiesFluxxUrl2, setTaskPrioritiesFluxxUrl2] =
    useState("");
  const [taskPrioritiesUserFilter, setTaskPrioritiesUserFilter] =
    useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    const config = await getWidgetConfigurations();

    const taskPrioritiesConfig = config?.[FEATURE_CODE] || {};

    setSavedConfig(taskPrioritiesConfig);

    setTaskPrioritiesFluxxUrl1(
      taskPrioritiesConfig.TaskPrioritiesFluxx1 || "",
    );

    setTaskPrioritiesFluxxUrl2(
      taskPrioritiesConfig.TaskPrioritiesFluxx2 || "",
    );

    setTaskPrioritiesUserFilter(
      taskPrioritiesConfig.TaskPrioritiesUserFilter === true ||
        taskPrioritiesConfig.TaskPrioritiesUserFilter === 1 ||
        taskPrioritiesConfig.TaskPrioritiesUserFilter === "1" ||
        taskPrioritiesConfig.TaskPrioritiesUserFilter === "true",
    );
  };

  const hasChanges =
    !!savedConfig &&
    (
      taskPrioritiesFluxxUrl1 !==
        (savedConfig.TaskPrioritiesFluxx1 || "") ||
      taskPrioritiesFluxxUrl2 !==
        (savedConfig.TaskPrioritiesFluxx2 || "") ||
      taskPrioritiesUserFilter !==
        !!(
          savedConfig.TaskPrioritiesUserFilter === true ||
          savedConfig.TaskPrioritiesUserFilter === 1 ||
          savedConfig.TaskPrioritiesUserFilter === "1" ||
          savedConfig.TaskPrioritiesUserFilter === "true"
        )
    );

  const handleSave = async () => {
    setSaving(true);

    try {
      const payload = {
        featureCode: FEATURE_CODE,
        configs: {
          TaskPrioritiesFluxx1: taskPrioritiesFluxxUrl1,
          TaskPrioritiesFluxx2: taskPrioritiesFluxxUrl2,
          TaskPrioritiesUserFilter: taskPrioritiesUserFilter,
        },
      };

      await updateWidgetConfigurations(payload);

      setSavedConfig(payload.configs);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!savedConfig) return;

    setTaskPrioritiesFluxxUrl1(
      savedConfig.TaskPrioritiesFluxx1 || "",
    );

    setTaskPrioritiesFluxxUrl2(
      savedConfig.TaskPrioritiesFluxx2 || "",
    );

    setTaskPrioritiesUserFilter(
      savedConfig.TaskPrioritiesUserFilter === true ||
        savedConfig.TaskPrioritiesUserFilter === 1 ||
        savedConfig.TaskPrioritiesUserFilter === "1" ||
        savedConfig.TaskPrioritiesUserFilter === "true",
    );
  };

  return (
    <div className="border border-gray-200 rounded-md bg-[#fbfaf7] p-3 space-y-3">
      <div className="flex items-center gap-4 text-xs">
        <span className="w-44 text-gray-700">
          Task Priorities User Filter
        </span>

        <Toggle
          enabled={taskPrioritiesUserFilter}
          onToggle={setTaskPrioritiesUserFilter}
        />
      </div>

      <div className="flex items-center gap-4 text-xs">
        <span className="w-44 text-gray-700">
          Fluxx URL (for each record)
        </span>

        <input
          type="text"
          value={taskPrioritiesFluxxUrl1}
          onChange={(e) =>
            setTaskPrioritiesFluxxUrl1(e.target.value)
          }
          className="flex-1 border border-gray-300 rounded px-3 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
          placeholder="Enter Fluxx URL"
        />
      </div>

      <div className="flex items-center gap-4 text-xs">
        <span className="w-44 text-gray-700">
          Fluxx URL (view in Fluxx)
        </span>

        <input
          type="text"
          value={taskPrioritiesFluxxUrl2}
          onChange={(e) =>
            setTaskPrioritiesFluxxUrl2(e.target.value)
          }
          className="flex-1 border border-gray-300 rounded px-3 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
          placeholder="Enter Fluxx URL"
        />
      </div>

      <div className="flex justify-end gap-2 items-center pt-2">
        {hasChanges && (
          <div className="text-orange-600 border border-orange-300 bg-orange-50 px-2 py-0.5 rounded text-xs">
            Unsaved
          </div>
        )}

        <button
          className="bg-[#e7e5e0] px-3 py-1 text-xs"
          onClick={handleCancel}
          disabled={!hasChanges}
        >
          Cancel
        </button>

        <button
          className="bg-[var(--color-brand)] text-white px-3 py-1 text-xs"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default TaskPrioritiesConfig;